import {
  DATA_LEVELS,
  MODULE_LEVELS,
  type DataField,
  type Module,
  type PermissionOverrides,
  type Permissions,
} from "@kickwin/shared";
import { cn } from "@/lib/utils";

/**
 * The permission checkboxes (decision 42): one row per module and per kind of
 * sensitive data, one button per level. Used for role templates and, with a
 * "Same as role" choice, for one person's exceptions. Levels above what the
 * editor holds are shown but cannot be picked — the server refuses them too.
 */

export const MODULE_LABELS: Record<Module, { label: string; hint: string }> = {
  overview: { label: "Overview", hint: "Numbers and alerts for the bot" },
  players: { label: "Players", hint: "Player list, details and bans" },
  sources: { label: "Sources", hint: "Influencer links and their counts" },
  broadcasts: { label: "Broadcasts", hint: "Campaigns to many players" },
  autoReplies: { label: "Auto reply", hint: "Automatic answers" },
  commands: { label: "Commands", hint: "The bot's /commands" },
  footer: { label: "Bottom menu", hint: "Buttons under the text box" },
  groups: { label: "CS group", hint: "Which group CS works in" },
  import: { label: "Import", hint: "Bring in players from a CSV" },
  systemLog: { label: "System log", hint: "What the bot did or could not do" },
  auditLog: { label: "Audit log", hint: "Who changed what" },
  bots: { label: "Bots", hint: "Every bot in the account" },
  admins: { label: "Admins & roles", hint: "Who can use this panel" },
};

export const LEVEL_LABELS: Record<string, string> = {
  none: "No access",
  view: "View",
  ban: "View & ban",
  manage: "Manage",
  draft: "Write drafts",
  send: "Send",
  use: "Import",
};

export const DATA_LABELS: Record<DataField, { label: string; hint: string }> = {
  playerName: { label: "Player name", hint: "First and last name" },
  telegramId: { label: "Telegram ID", hint: "The player's Telegram number" },
  username: { label: "Username", hint: "The player's @username" },
  messageContent: { label: "Message content", hint: "Text, photos and files in history" },
  adminEmail: { label: "Admin emails", hint: "Who did what, in logs" },
};

export const DATA_LEVEL_LABELS: Record<string, string> = {
  hidden: "Hidden",
  masked: "Masked ab***",
  full: "Full",
};

const BOT_MODULES = (Object.keys(MODULE_LEVELS) as Module[]).filter(
  (m) => m !== "bots" && m !== "admins",
);
const ACCOUNT_MODULES: Module[] = ["bots", "admins"];

type Mode =
  | { kind: "role"; value: Permissions; onChange: (next: Permissions) => void }
  | {
      kind: "overrides";
      base: Permissions;
      value: PermissionOverrides;
      onChange: (next: PermissionOverrides) => void;
    };

export function PermissionMatrix({
  limit,
  disabled,
  ...mode
}: Mode & {
  /** The editor's own permissions; anything above is not selectable. */
  limit: Permissions | null;
  disabled?: boolean;
}) {
  const moduleValue = (m: Module): string | undefined =>
    mode.kind === "role" ? mode.value.modules[m] : mode.value.modules?.[m];
  const dataValue = (f: DataField): string | undefined =>
    mode.kind === "role" ? mode.value.data[f] : mode.value.data?.[f];

  const setModule = (m: Module, level: string | undefined) => {
    if (mode.kind === "role") {
      mode.onChange({ ...mode.value, modules: { ...mode.value.modules, [m]: level } });
    } else {
      const modules = { ...(mode.value.modules ?? {}) } as Record<string, string>;
      if (level === undefined) delete modules[m];
      else modules[m] = level;
      mode.onChange({ ...mode.value, modules: modules as PermissionOverrides["modules"] });
    }
  };
  const setData = (f: DataField, level: string | undefined) => {
    if (mode.kind === "role") {
      mode.onChange({ ...mode.value, data: { ...mode.value.data, [f]: level } });
    } else {
      const data = { ...(mode.value.data ?? {}) } as Record<string, string>;
      if (level === undefined) delete data[f];
      else data[f] = level;
      mode.onChange({ ...mode.value, data: data as PermissionOverrides["data"] });
    }
  };

  const above = (levels: readonly string[], level: string, max: string | undefined) =>
    max !== undefined && levels.indexOf(level) > levels.indexOf(max);

  const moduleRow = (m: Module) => {
    const levels = MODULE_LEVELS[m] as readonly string[];
    return (
      <Row
        key={m}
        label={MODULE_LABELS[m].label}
        hint={MODULE_LABELS[m].hint}
        options={levels.map((l) => ({
          value: l,
          label: LEVEL_LABELS[l] ?? l,
          disabled: disabled || above(levels, l, limit?.modules[m]),
        }))}
        value={moduleValue(m)}
        onChange={(l) => setModule(m, l)}
        roleDefault={
          mode.kind === "overrides" ? (LEVEL_LABELS[mode.base.modules[m]] ?? "") : undefined
        }
        disabled={disabled}
      />
    );
  };

  return (
    <div className="grid gap-6">
      <Section title="On each bot they are given">{BOT_MODULES.map(moduleRow)}</Section>
      <Section title="Sensitive data">
        {(Object.keys(DATA_LEVELS) as DataField[]).map((f) => {
          const levels = DATA_LEVELS[f] as readonly string[];
          return (
            <Row
              key={f}
              label={DATA_LABELS[f].label}
              hint={DATA_LABELS[f].hint}
              options={levels.map((l) => ({
                value: l,
                label: DATA_LEVEL_LABELS[l] ?? l,
                disabled: disabled || above(levels, l, limit?.data[f]),
              }))}
              value={dataValue(f)}
              onChange={(l) => setData(f, l)}
              roleDefault={
                mode.kind === "overrides" ? (DATA_LEVEL_LABELS[mode.base.data[f]] ?? "") : undefined
              }
              disabled={disabled}
            />
          );
        })}
      </Section>
      <Section title="Whole account">{ACCOUNT_MODULES.map(moduleRow)}</Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1">
      <div className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
        {title}
      </div>
      <div className="divide-y divide-border rounded-lg border border-border">{children}</div>
    </div>
  );
}

function Row({
  label,
  hint,
  options,
  value,
  onChange,
  roleDefault,
  disabled,
}: {
  label: string;
  hint: string;
  options: { value: string; label: string; disabled: boolean }[];
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  /** Present in exceptions mode: the role's own level, offered as "Same as role". */
  roleDefault?: string | undefined;
  disabled?: boolean | undefined;
}) {
  const overridden = roleDefault !== undefined && value !== undefined;
  return (
    <div className="grid gap-2 px-4 py-3 md:grid-cols-[minmax(0,220px)_1fr] md:items-center">
      <div className="min-w-0">
        <div className="flex items-center gap-2 font-medium">
          {label}
          {overridden && (
            <span className="rounded-full bg-accent px-1.5 py-0.5 text-[11px] font-medium text-accent-foreground">
              Exception
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
        {roleDefault !== undefined && (
          <Choice
            selected={value === undefined}
            disabled={disabled}
            onClick={() => onChange(undefined)}
          >
            Same as role · {roleDefault}
          </Choice>
        )}
        {options.map((o) => (
          <Choice
            key={o.value}
            selected={value === o.value}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </Choice>
        ))}
      </div>
    </div>
  );
}

function Choice({
  selected,
  disabled,
  onClick,
  children,
}: {
  selected: boolean;
  disabled?: boolean | undefined;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "rounded-md border px-2.5 py-1 text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        selected
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card hover:border-ring/60",
      )}
    >
      {children}
    </button>
  );
}

/** How many choices an exception set actually changes. */
export function exceptionCount(overrides: PermissionOverrides | null | undefined): number {
  return Object.keys(overrides?.modules ?? {}).length + Object.keys(overrides?.data ?? {}).length;
}
